import * as React from "react"
import { Control, Emitter } from "rete"
import { EventsTypes } from "rete/types/events"

export interface DateRangePreset {
    id: string
    label: string
    /** Inclusive number of days ending today (UTC). 1 = today only. */
    days: number
}

interface PresetButtonsProps {
    emitter: Emitter<EventsTypes> | null
    presets: DateRangePreset[]
    getFrom: () => string
    getTo: () => string
    applyPreset: (preset: DateRangePreset) => void
}

type EmitterWithOff = Emitter<EventsTypes> & {
    off: (event: string, handler: (...args: unknown[]) => void) => void
}

const hasOff = (emitter: Emitter<EventsTypes> | null): emitter is EmitterWithOff =>
    !!emitter && typeof (emitter as Partial<EmitterWithOff>).off === "function"

function isoUtcDate(offsetDays: number): string {
    const d = new Date()
    d.setUTCDate(d.getUTCDate() + offsetDays)
    return d.toISOString().slice(0, 10)
}

export function rangeForPreset(preset: DateRangePreset): { from: string; to: string } {
    const to = isoUtcDate(0)
    const from = isoUtcDate(1 - preset.days)
    return { from, to }
}

const PresetButtons = ({ emitter, presets, getFrom, getTo, applyPreset }: PresetButtonsProps) => {
    const [, forceUpdate] = React.useReducer(x => x + 1, 0)

    React.useEffect(() => {
        if (!emitter) return
        const handler = () => setTimeout(() => forceUpdate(), 0)
        emitter.on("process", handler)
        return () => {
            if (hasOff(emitter)) emitter.off("process", handler)
        }
    }, [emitter])

    const from = getFrom()
    const to = getTo()
    const activeId = presets.find(preset => {
        const range = rangeForPreset(preset)
        return range.from === from && range.to === to
    })?.id

    return (
        <div className="preset-buttons-control" role="group" aria-label="Date range presets">
            {presets.map(preset => {
                const active = preset.id === activeId
                return (
                    <button
                        type="button"
                        key={preset.id}
                        className={`preset-buttons-control__btn${active ? " is-active" : ""}`}
                        aria-pressed={active}
                        onClick={event => {
                            event.stopPropagation()
                            applyPreset(preset)
                            forceUpdate()
                        }}
                        onPointerDown={event => event.stopPropagation()}
                        onDoubleClick={event => event.stopPropagation()}
                    >
                        {preset.label}
                    </button>
                )
            })}
        </div>
    )
}

export class PresetButtonsControl extends Control {
    props: PresetButtonsProps
    component: (props: PresetButtonsProps) => JSX.Element
    type: string

    constructor(
        emitter: Emitter<EventsTypes> | null,
        key: string,
        fromKey: string,
        toKey: string,
        presets: DateRangePreset[]
    ) {
        super(key)
        this.type = "PresetButtonsControl"

        const process = () => emitter?.trigger("process")
        this.props = {
            emitter,
            presets,
            getFrom: () => {
                const value = this.getData(fromKey)
                return typeof value === "string" ? value : ""
            },
            getTo: () => {
                const value = this.getData(toKey)
                return typeof value === "string" ? value : ""
            },
            applyPreset: preset => {
                const { from, to } = rangeForPreset(preset)
                this.putData(fromKey, from)
                this.putData(toKey, to)
                this.putData(key, preset.id)
                process()
                const node = this.parent as { update?: () => unknown } | null
                if (node && typeof node.update === "function") {
                    void node.update()
                }
            },
        }
        this.component = PresetButtons
    }
}
